import { createDraftSafeSelector } from '@reduxjs/toolkit'
import { shallowEqual } from 'react-redux'
import { Indicator } from '../proto/generated/indicator'
import {
    SuperChartConfiguration_ChartType,
    SuperChartConfiguration_DateRange,
    SuperChartConfiguration_TimeBucket
} from '../proto/generated/superchart-page'
import { ChartStateType } from '../reducer/chart.reducer'
import { RootState } from '../store'
import { ChartConfig } from '../components/chartV2/chart.typings'
import { ChartType } from '../proto/generated/chart'
import { ChartDrawingItem, DrawingDisplayType } from '../components/chartV2/drawingTools/drawing.interface'
import { LineType } from 'lightweight-charts'
import {
    CallPutType,
    MultiStrikeChartStylesTypes,
    MultiStrikeChartTypes,
    MultiStrikeSecuritiesType,
} from '../reducer/multiStrikeChart.reducer'
import { ExchangeTradingSession, TradingCalendarData } from '../proto/generated/trading-calendar'
import { Exchange } from '../proto/generated/common'
import { ChartViewKey } from '../assets/constants'
import { normalizeMultiStrikeColorPaletteId } from '../components/multiStikeChart/multiStrikeColorPalettes'
import { normalizeMultiStrikeChartType } from '../components/multiStikeChart/multistrikeChartTypeHelpers'
import { sanitizeId } from '../util/indicators'
import { getCachedParamSelector } from '../util/cachedParamSelector'
import { defaultScalesAndLinesSettings } from '../util/scalesAndLinesDefaults'
import { defaultStatusLineSettings } from '../util/statusLineDefaults'
import { defaultCanvasSettings } from '../util/canvasSettingsDefaults'
import { defaultOiWidgetSettings } from '../util/oiWidgetSettingsDefaults'
import { ScalesAndLinesSettings, StatusLineSettings, CanvasSettings, OiWidgetSettings } from '../components/chartV2/chart.typings'

const getChartConfig = (store: RootState): ChartStateType['chartConfig'] => store.chartStore.chartConfig

const multiStrikeConfigSelectorCache = new Map<string, (state: RootState) => unknown>()

const multiStrikeConfigFieldSelector = <T>(
    viewKey: string,
    field: string,
    read: (settings: NonNullable<ReturnType<typeof getChartConfig>[string]>['multiStrikeChartSettings']) => T,
    fallback?: T
): ((state: RootState) => T) =>
    getCachedParamSelector<T>(
        multiStrikeConfigSelectorCache as Map<string, (state: RootState) => T>,
        `${field}:${viewKey}`,
        (store) => {
            const settings = getChartConfig(store)[viewKey]?.multiStrikeChartSettings
            if (!settings) return fallback as T
            return read(settings)
        }
    )
const selectChartStoreSlice = (state: RootState) => state.chartStore
const getChartState = (store: RootState): ChartStateType => store.chartStore
const state = (state: RootState) => state
export const selectActiveChartId = createDraftSafeSelector(state, (store: RootState) => store.chartStore.activeChartId as string)
export const selectChartFocused = createDraftSafeSelector(state, (store: RootState) => store.chartStore.chartFocused as boolean)
export const selectCalendarEvents = createDraftSafeSelector(state, (store: RootState) => {
    const calenderData = store.chartStore.tradingCalenderData
    if (!calenderData) return []
    return (calenderData!! as TradingCalendarData).holidays
})

export const selectTradingCalendarDataByExchange = (exchange: Exchange) => createDraftSafeSelector(
    (state: RootState) => (state.chartStore as ChartStateType).tradingCalenderData,
    (calendarData) => calendarData.find((data: { exchange: Exchange }) => data.exchange === (exchange ?? Exchange.NSE))
)

export const selectTradingCalenderData = createDraftSafeSelector(state, (store: RootState) => {
    return store.chartStore.tradingCalenderData
})
export const selectChartTypes = createDraftSafeSelector(state, (store: RootState) => (store.chartStore.chartTypes || []) as SuperChartConfiguration_ChartType[])
export const selectChartConfig = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey])
export const selectChartConfigSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.statusLineSettings)
export const selectChartScalesAndLinesSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.scalesAndLinesSettings)
export const selectChartCanvasSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.canvasSettings)
export const selectChartTradingSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.tradingSettings)
export const selectChartAlertSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.alertSettings)
export const selectChartEventsSettings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.eventsSettings)
export const selectChartConfigs = createDraftSafeSelector(state, (store: RootState) => store.chartStore.chartConfig as Record<string, ChartConfig>)
export const selectChartType = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.chartType)
export const selectScreenerChartType = createDraftSafeSelector(state, (store: RootState) => store.chartStore.screenerChartConfigs?.chartType as ChartType)
export const selectScreenerSelectedDateRange = createDraftSafeSelector(state, (store: RootState) => store.chartStore.screenerChartConfigs?.selectedDateRange as SuperChartConfiguration_DateRange)
export const selectChartDrawings = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => {
    const drawings = store.chartStore.drawings[viewKey];
    if (!drawings) return;
    return drawings.filter((drawing: ChartDrawingItem) => drawing.displayType === DrawingDisplayType.DRAWING);
});
export const selectDrawings = createDraftSafeSelector(state, (store: RootState) => (store.chartStore.drawings || {}) as Record<string, ChartDrawingItem[]>)
export const selectChartPanesConfig = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.panesConfig || {})
export const selectChartPanes = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.panes || [])
export const selectChartComparableSecurities = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => (getChartConfig(store)[viewKey]?.comparableSecurities || []))
export const selectChartTimeBucket = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.timeBucket)
export const selectChartSeriesGroups = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.seriesGroups || {})
export const selectChartSeries = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.series || {})
export const selectChartOiProfileStatus = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.showOiProfile || false)
export const selectIndicator = (code: string) => createDraftSafeSelector(state, (store: RootState) => {
    const [indicator] = store.chartStore.indicators.filter((i: Indicator) => i.code === code)
    return indicator as Indicator
})
export const selectIndicators = createDraftSafeSelector(state, (store: RootState): Indicator[] => store.chartStore.indicators)
export const selectIndicatorScript = (code: string) => createDraftSafeSelector(state, (store: RootState) => store.chartStore.indicatorScripts[sanitizeId(code)])
export const selectTimeBuckets = createDraftSafeSelector(state, (store: RootState): SuperChartConfiguration_TimeBucket[] => store.chartStore.timeBuckets)
export const selectTimebucketsOpen = createDraftSafeSelector(state, (store: RootState): boolean => store.chartStore.timeBucketsOpen)
export const selectDateRanges = createDraftSafeSelector(state, (store: RootState): SuperChartConfiguration_DateRange[] => store.chartStore.dateRanges)
export const selectConfigDataLoaded = createDraftSafeSelector(state, (store: RootState): boolean => {
    if (store.chartStore.indicators?.length > 0 && store.chartStore.timeBuckets?.length > 0 && store.chartStore.chartTypes?.length > 0 && store.chartStore.dateRanges?.length > 0 && store.chartStore.exchangeTradingSessions?.length > 0) {
        return true
    }
    return false
})
export const selectExchangeTradingSessions = createDraftSafeSelector(state, (store: RootState) => store.chartStore.exchangeTradingSessions as ExchangeTradingSession[])
export const selectMultiStrikeChartConfig = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.multiStrikeChartSettings)
const defaultCallPut: CallPutType[] = ['CE', 'PE']

export const selectMultiStrikeChartConfigSecurities = (viewKey: string) =>
    multiStrikeConfigFieldSelector<MultiStrikeSecuritiesType | undefined>(viewKey, 'securities', (s) => s.securities)
export const selectMultiStrikeChartConfigChartType = (viewKey: string) =>
    multiStrikeConfigFieldSelector<MultiStrikeChartTypes>(
        viewKey,
        'chartType',
        (s) => normalizeMultiStrikeChartType(s.chartType, s.showChange),
        'iv'
    )
const MultiStrikeDefaultStyles: MultiStrikeChartStylesTypes = {
    lineThickness: 1,
    lineType: LineType.Simple,
    autoRefresh: true,
    notifyInterval: 30,
    nearAtmCount: 3,
    colorPaletteId: 'default',
    customCallBaseColor: '#2F2FCD',
    customPutBaseColor: '#FFA719',
    customCallHueShift: 50,
    customPutHueShift: 50,
    underlyingThickness: 1,
    underlyingLineStyle: 'large_dashed',
    underlyingLineType: LineType.Simple,
    strikeLineStyle: 'solid',
};
export const selectMultiStrikeChartConfigStyles = (viewKey: string) => {
    const cacheKey = `styles:${viewKey}`
    let selector = multiStrikeConfigSelectorCache.get(cacheKey) as ((state: RootState) => MultiStrikeChartStylesTypes) | undefined
    if (!selector) {
        selector = createDraftSafeSelector(
            (store: RootState) => getChartConfig(store)[viewKey]?.multiStrikeChartSettings?.styles,
            (rawStyles) => ({
                ...MultiStrikeDefaultStyles,
                ...(rawStyles ?? {}),
                colorPaletteId: normalizeMultiStrikeColorPaletteId(rawStyles?.colorPaletteId),
                customCallBaseColor: rawStyles?.customCallBaseColor ?? MultiStrikeDefaultStyles.customCallBaseColor,
                customPutBaseColor: rawStyles?.customPutBaseColor ?? MultiStrikeDefaultStyles.customPutBaseColor,
                customCallHueShift: rawStyles?.customCallHueShift ?? MultiStrikeDefaultStyles.customCallHueShift,
                customPutHueShift: rawStyles?.customPutHueShift ?? MultiStrikeDefaultStyles.customPutHueShift,
            }),
            { memoizeOptions: { resultEqualityCheck: shallowEqual } }
        )
        multiStrikeConfigSelectorCache.set(cacheKey, selector)
    }
    return selector
}
export const selectMultiStrikeChartConfigCallPut = (viewKey: string) =>
    multiStrikeConfigFieldSelector<CallPutType[]>(viewKey, 'callPut', (s) => s.callPut ?? defaultCallPut, defaultCallPut)
export const selectMultiStrikeChartConfigCumOi = (viewKey: string) =>
    multiStrikeConfigFieldSelector<boolean>(viewKey, 'cumOi', (s) => s.cumOi ?? false, false)
export const selectMultiStrikeChartConfigShowChange = (viewKey: string) =>
    multiStrikeConfigFieldSelector<boolean>(viewKey, 'showChange', (s) => s.showChange ?? false, false)
export const selectMultiStrikeChartConfigNearAtm = (viewKey: string) =>
    multiStrikeConfigFieldSelector<number | undefined>(viewKey, 'nearAtm', (s) => s.nearAtm)
export const selectMultiStrikeChartConfigAtm = (viewKey: string) =>
    multiStrikeConfigFieldSelector<number | undefined>(viewKey, 'atm', (s) => s.atm)
export const selectMultiStrikeChartConfigExpiry = (viewKey: string) =>
    multiStrikeConfigFieldSelector<string | undefined>(viewKey, 'expiry', (s) => s.expiry)
export const selectMultiStrikeConfigChartKey = (viewKey: string) =>
    multiStrikeConfigFieldSelector<string | undefined>(viewKey, 'key', (s) => s.key)
export const selectFutureConfigChartKey = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.futureChartSettings?.key)
export const selectFutureConfigChartSecurityKey = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.futureChartSettings?.securityKey)
export const selectScreenerChartDateRange = createDraftSafeSelector(state, (store: RootState) => store.chartStore.screenerChartConfigs.selectedDateRange as SuperChartConfiguration_DateRange)
export const selectChartReplayMode = createDraftSafeSelector(state, (store: RootState) => getChartState(store).replayMode)
export const selectAtmOptionConfigChartKey = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.atmOptionChartSettings?.key)
export const selectAtmOptionConfigChartSecurityKey = (viewKey: string) => createDraftSafeSelector(state, (store: RootState) => getChartConfig(store)[viewKey]?.atmOptionChartSettings?.securityKey)
export const selectChartDateRanges = createDraftSafeSelector(state, (state: RootState) => state.chartStore.dateRanges as SuperChartConfiguration_DateRange[])

/** Only re-subscribes when `chartStore.dateRanges` changes (not on unrelated chart ticks). */
export const selectOverviewOneDayDateRange = createDraftSafeSelector(
    (state: RootState) => state.chartStore.dateRanges,
    (dateRanges) => dateRanges?.find((range) => range.code === 'ONE_DAY')
)

/** Sidebar overview chart — input is only `chartStore`, so snapshot/order ticks do not invalidate selectors. */
export const selectOverviewSidebarChartConfig = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]
)
export const selectOverviewSidebarChartPanes = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.panes || []
)
export const selectOverviewSidebarChartPanesConfig = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.panesConfig || {}
)
export const selectOverviewSidebarChartSeriesGroups = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.seriesGroups || {}
)
export const selectOverviewSidebarChartSeries = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.series || {}
)
export const selectOverviewSidebarChartType = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.chartType
)
export const selectOverviewSidebarChartTimeBucket = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.chartConfig[ChartViewKey.OVERVIEW]?.timeBucket
)
export const selectChartStoreTimeBuckets = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.timeBuckets
)
export const selectChartStoreIndicators = createDraftSafeSelector(
    selectChartStoreSlice,
    (chartStore) => chartStore.indicators
)

const multiStrikeScalesAndLinesSelectorCache = new Map<string, (state: RootState) => ScalesAndLinesSettings>()

export const selectMultiStrikeScalesAndLinesSettings = (viewKey: string) => {
    const cacheKey = `scalesAndLines:${viewKey}`
    let selector = multiStrikeScalesAndLinesSelectorCache.get(cacheKey)
    if (!selector) {
        const isWidget = viewKey !== ChartViewKey.MULTI_STRIKES_CHART
        if (isWidget) {
            selector = createDraftSafeSelector(
                (store: RootState) => getChartConfig(store)[viewKey]?.scalesAndLinesSettings,
                (settings) => ({
                    ...defaultScalesAndLinesSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        } else {
            selector = createDraftSafeSelector(
                (store: RootState) => store.multiStrikeChartStore.scalesAndLinesSettings,
                (settings) => ({
                    ...defaultScalesAndLinesSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        }
        multiStrikeScalesAndLinesSelectorCache.set(cacheKey, selector)
    }
    return selector
}

const multiStrikeStatusLineSelectorCache = new Map<string, (state: RootState) => StatusLineSettings>()

export const selectMultiStrikeStatusLineSettings = (viewKey: string) => {
    const cacheKey = `statusLine:${viewKey}`
    let selector = multiStrikeStatusLineSelectorCache.get(cacheKey)
    if (!selector) {
        const isWidget = viewKey !== ChartViewKey.MULTI_STRIKES_CHART
        if (isWidget) {
            selector = createDraftSafeSelector(
                (store: RootState) => getChartConfig(store)[viewKey]?.statusLineSettings,
                (settings) => ({
                    ...defaultStatusLineSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        } else {
            selector = createDraftSafeSelector(
                (store: RootState) => store.multiStrikeChartStore.statusLineSettings,
                (settings) => ({
                    ...defaultStatusLineSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        }
        multiStrikeStatusLineSelectorCache.set(cacheKey, selector)
    }
    return selector
}

const multiStrikeCanvasSelectorCache = new Map<string, (state: RootState) => CanvasSettings>()

export const selectMultiStrikeCanvasSettings = (viewKey: string) => {
    const cacheKey = `canvas:${viewKey}`
    let selector = multiStrikeCanvasSelectorCache.get(cacheKey)
    if (!selector) {
        const isWidget = viewKey !== ChartViewKey.MULTI_STRIKES_CHART
        if (isWidget) {
            selector = createDraftSafeSelector(
                (store: RootState) => getChartConfig(store)[viewKey]?.canvasSettings,
                (settings) => ({
                    ...defaultCanvasSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        } else {
            selector = createDraftSafeSelector(
                (store: RootState) => store.multiStrikeChartStore.canvasSettings,
                (settings) => ({
                    ...defaultCanvasSettings,
                    ...(settings ?? {}),
                }),
                { memoizeOptions: { resultEqualityCheck: shallowEqual } }
            )
        }
        multiStrikeCanvasSelectorCache.set(cacheKey, selector)
    }
    return selector
}

const oiWidgetSettingsSelectorCache = new Map<string, (state: RootState) => OiWidgetSettings>()

export const selectOiWidgetSettings = (viewKey: string) => {
    const cacheKey = `oiWidget:${viewKey}`
    let selector = oiWidgetSettingsSelectorCache.get(cacheKey)
    if (!selector) {
        selector = createDraftSafeSelector(
            (store: RootState) => getChartConfig(store)[viewKey]?.oiWidgetSettings,
            (settings) => ({
                ...defaultOiWidgetSettings,
                ...(settings ?? {}),
            }),
            { memoizeOptions: { resultEqualityCheck: shallowEqual } }
        )
        oiWidgetSettingsSelectorCache.set(cacheKey, selector)
    }
    return selector
}
